package com.elan.edubridge.ui.shell

import com.elan.edubridge.shared.domain.model.SessionState
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent

/** 매치된 라우트가 핸들러로 받는 컨텍스트. */
class RouteContext(
    val path: String,
    val params: Map<String, String>,
    val query: Map<String, String>,
)

private class RouteEntry(val segments: List<String>, val handler: (RouteContext) -> Unit)

/**
 * History API 기반 클라이언트 라우터(해시 라우팅이 아니다).
 *
 * **선택 근거**: `/qr?a=&s=&n=`는 QR 스캔·카메라 앱이 실제로 여는 절대 URL이라 해시로는 표현할 수 없고,
 * `firebase.json` hosting이 이미 모든 경로를 `index.html`로 rewrite하도록 구성돼 있어(`"source": "**"`)
 * 새로고침·직접 진입에도 안전하다(별도 인프라 변경 불필요).
 *
 * [register]가 C↔D 유일 접점이다 — S7(회원 뷰)은 이 함수로 `/app/me` 하위 라우트를 추가 등록하면 된다.
 * 패턴 문법은 가이드 마크업이 이미 쓰는 `{param}` 표기를 그대로 따른다(예: `/app/{academyId}/{section}`).
 *
 * **등록 순서가 매치 우선순위다(첫 매치 승리).** 세그먼트 개수가 같은 라우트끼리만 충돌할 수 있으므로,
 * 리터럴 라우트는 그와 세그먼트 개수가 같은 동적 라우트보다 먼저 등록해야 한다
 * (예: `/app/admin/applications`는 3세그먼트 — 역시 3세그먼트인 `/app/{academyId}/{section}`보다 먼저 등록).
 *
 * **리뷰 후속(M15·M17·M18)**: ① `decodeURIComponent`는 잘못된 `%` 이스케이프 시퀀스에 예외를 던질 수
 * 있어 전 지점을 try/catch로 감쌌다(실패한 파라미터는 조용히 버린다) ② `history.pushState`/
 * `replaceState`는 일부 브라우저 보안 컨텍스트(크로스 오리진 iframe 등)에서 `SecurityError`를 던질 수
 * 있어 방어했다 ③ [dispatch] 전체를 top-level try/catch로 감쌌다 — 라우트 핸들러가 던진 예외가
 * `Main.kt`의 유일한 `session.collect` 구독까지 죽이면 이후 세션이 바뀌어도 화면이 다시는 갱신되지
 * 않는다(치명적). 지금은 오류 화면을 그리고 구독은 살려 둔다 ④ [setCleanup]은 페이지 이탈(다음
 * dispatch) 시 인터벌·구독 등을 정리할 훅이 필요한 화면(키오스크 등)을 위한 최소 확장점이다.
 */
object Router {
    private val routes = mutableListOf<RouteEntry>()
    private var notFound: (RouteContext) -> Unit = { navigate("/") }
    private var listenersWired = false
    private var cleanup: (() -> Unit)? = null

    fun register(pattern: String, handler: (RouteContext) -> Unit) {
        routes += RouteEntry(segmentsOf(pattern), handler)
    }

    fun setNotFound(handler: (RouteContext) -> Unit) {
        notFound = handler
    }

    /**
     * 현재 화면이 다음 라우트로 넘어가기 전에 정리해야 할 자원(인터벌, WakeLock, 구독 등)이 있으면
     * 등록한다. 다음 [dispatch] 직전에 정확히 한 번 호출되고 자동으로 비워진다 — 새 페이지가 필요하면
     * 자기 것을 다시 등록해야 한다. `null`을 넘기면 예약된 정리 작업을 취소한다.
     */
    fun setCleanup(fn: (() -> Unit)?) {
        cleanup = fn
    }

    /**
     * 링크 가로채기·popstate 리스너 배선은 최초 1회만 하고, 호출할 때마다 현재 경로를 재디스패치한다.
     * Main.kt는 세션 스트림이 방출할 때마다(최초 복원 포함) 이 함수를 부른다 — 별도의
     * "초기 렌더" 분기가 필요 없고, 세션이 바뀌면 항상 같은 경로를 최신 세션으로 다시 그린다.
     */
    fun start() {
        if (!listenersWired) {
            listenersWired = true
            document.addEventListener("click", { event -> onDocumentClick(event) })
            window.addEventListener("popstate", { dispatchCurrent() })
        }
        dispatchCurrent()
    }

    /** 새 히스토리 항목을 쌓는 이동(사용자 액션의 결과 — 링크 클릭 등). */
    fun navigate(path: String) {
        try {
            window.history.pushState(null, "", path)
        } catch (error: Throwable) {
            // SecurityError 등 — 주소창은 갱신되지 않아도 화면은 최소한 반응해야 한다.
            console.error("[Router] pushState 실패, 그대로 디스패치만 진행", error)
        }
        dispatchCurrent()
    }

    /** 히스토리를 쌓지 않는 이동 — `/app` 허브 리다이렉트, 권한 밖 접근 안전망 등에 쓴다. */
    fun replace(path: String) {
        try {
            window.history.replaceState(null, "", path)
        } catch (error: Throwable) {
            console.error("[Router] replaceState 실패, 그대로 디스패치만 진행", error)
        }
        dispatchCurrent()
    }

    fun dispatchCurrent() {
        dispatch(window.location.pathname, window.location.search)
    }

    private fun dispatch(pathname: String, search: String) {
        val previousCleanup = cleanup
        cleanup = null
        try {
            previousCleanup?.invoke()
        } catch (error: Throwable) {
            console.error("[Router] 이전 화면 정리(cleanup) 중 오류", error)
        }

        try {
            val actual = segmentsOf(pathname)
            val query = parseQuery(search)
            for (route in routes) {
                val params = match(route.segments, actual) ?: continue
                route.handler(RouteContext(pathname, params, query))
                return
            }
            notFound(RouteContext(pathname, emptyMap(), query))
        } catch (error: Throwable) {
            // 라우트 핸들러 예외가 여기서 멈추지 않으면 Main.kt의 session.collect까지 죽는다 —
            // 그러면 이후 로그인·로그아웃이 다시는 화면에 반영되지 않는 치명적 상태가 된다.
            console.error("[Router] $pathname 디스패치 실패", error)
            renderDispatchError()
        }
    }

    private fun renderDispatchError() {
        val root = appRoot()
        root.className = "eb-legal"
        root.innerHTML = """
            <div class="eb-empty-state">
                ${icon("icon-warning", "xl")}
                <h2>문제가 발생했어요</h2>
                <p>페이지를 표시하지 못했어요. 홈으로 돌아가 다시 시도해 주세요.</p>
                <a class="eb-button eb-button--primary" href="/">홈으로 이동</a>
            </div>
        """.trimIndent()
    }

    private fun match(pattern: List<String>, actual: List<String>): Map<String, String>? {
        if (pattern.size != actual.size) return null
        val params = mutableMapOf<String, String>()
        pattern.forEachIndexed { index, part ->
            val value = actual[index]
            when {
                part.startsWith("{") && part.endsWith("}") -> params[part.substring(1, part.length - 1)] = value
                part != value -> return null
            }
        }
        return params
    }

    private fun segmentsOf(path: String): List<String> =
        path.substringBefore("?").trim('/').split("/").filter { it.isNotEmpty() }

    private fun onDocumentClick(event: Event) {
        val mouseEvent = event as? MouseEvent ?: return
        if (mouseEvent.defaultPrevented) return
        if (mouseEvent.button.toInt() != 0) return
        if (mouseEvent.metaKey || mouseEvent.ctrlKey || mouseEvent.shiftKey || mouseEvent.altKey) return

        var node = event.target as? HTMLElement
        while (node != null && node !is HTMLAnchorElement) {
            node = node.parentElement as? HTMLElement
        }
        val anchor = node as? HTMLAnchorElement ?: return
        if (anchor.target.isNotEmpty() || anchor.hasAttribute("download")) return

        val href = anchor.getAttribute("href") ?: return
        // 동일 출처 절대 경로(`/`로 시작)만 가로챈다. 해시 앵커(#features)·외부 링크·mailto는 브라우저 기본 동작에 맡긴다.
        if (!href.startsWith("/")) return

        event.preventDefault()
        navigate(href)
    }
}

private fun parseQuery(search: String): Map<String, String> {
    val raw = search.removePrefix("?")
    if (raw.isEmpty()) return emptyMap()
    return raw.split("&").filter { it.isNotEmpty() }.mapNotNull { pair ->
        val idx = pair.indexOf("=")
        try {
            if (idx < 0) {
                decodeURIComponent(pair) to ""
            } else {
                decodeURIComponent(pair.substring(0, idx)) to decodeURIComponent(pair.substring(idx + 1))
            }
        } catch (error: Throwable) {
            // 잘못된 `%` 이스케이프 시퀀스(예: `%zz`)는 decodeURIComponent가 URIError를 던진다 —
            // 그 한 쌍만 조용히 버리고 나머지 쿼리 파싱은 계속한다.
            null
        }
    }.toMap()
}

private external fun decodeURIComponent(encodedURI: String): String
private external fun encodeURIComponent(input: String): String

/** 로그인 여부만 보는 가드. `/app` 하위 라우트가 공통으로 쓴다(D lane도 그대로 재사용 가능). */
fun requireSession(render: (SessionState, RouteContext) -> Unit): (RouteContext) -> Unit = { ctx ->
    val session = SessionStore.current
    when {
        !session.isAuthenticated -> {
            Router.replace("/login?redirect=${encodeURIComponent(ctx.path)}")
        }
        session.requiresGuardianConsent -> {
            Router.replace("/app/guardian-consent")
        }
        else -> render(session, ctx)
    }
}

/** 랜딩·로그인·회원가입처럼 "로그인하지 않은 방문자 전용"(가이드 §3.1) 라우트가 쓰는 가드.
 * 이미 인증된 사용자가 들어오면 `redirect` 쿼리(§3.3 `/login?redirect=`)가 있으면 그곳으로,
 * 없으면 `/app` 허브로 되돌려 재판정시킨다. */
fun publicOnly(render: (RouteContext) -> Unit): (RouteContext) -> Unit = { ctx ->
    if (SessionStore.current.isAuthenticated) {
        Router.replace(safeRedirectTarget(ctx.query["redirect"]))
    } else {
        render(ctx)
    }
}

/**
 * `redirect` 쿼리는 사용자가 클릭한 링크(혹은 조작된 URL)에서 오므로 동일 출처 상대 경로만 허용한다.
 * - `//evil.example`처럼 프로토콜-상대 URL로 다른 origin에 보내는 오픈 리다이렉트를 막는다.
 * - `\evil.example`·`/\evil.example`도 막는다 — 일부 브라우저는 히스토리 API·후속 네비게이션에서
 *   백슬래시를 슬래시처럼 취급해 `//`와 동일한 프로토콜-상대 우회로 악용될 수 있다(M15).
 */
private fun safeRedirectTarget(redirect: String?): String =
    if (redirect != null && redirect.startsWith("/") && !redirect.startsWith("//") && !redirect.contains('\\')) {
        redirect
    } else {
        "/app"
    }
